import React, { useMemo, useState, useCallback, useLayoutEffect } from 'react';
import { ThemeProvider, createTheme } from '@material-ui/core/styles';
import { Box } from '@material-ui/core';
import { useLocalizedMessage } from '../../../util/useLocalizedMessage';
import styles from './WidgetPreview.module.scss';
import SearchBoxShell from './shells/SearchBoxShell';
import ResultsPageShell from './shells/ResultsPageShell';
import DropdownShell from './shells/DropdownShell';
import CanvasRenderer from '../core/renderer/CanvasRenderer';
import CanvasResolver from '../core/renderer/CanvasResolver';
import { resolvePreviewFacetPresentation } from './previewSurface';
import { ResponsiveFrame } from './DeviceFrames';
import { keaPalette } from 'styles/designTokens';

/**
 * Main orchestrator for the preview area.
 * Handles:
 * 1. Selection of the appropriate environment shell (SearchBox vs ResultsPage)
 * 2. Injection of the user's theme into the widget context
 * 3. Rendering the Canvas
 */
const WidgetPreview = ({
  canvas,
  viewConfig,
  onPresentationChange,
  theme: configTheme,
  viewType = 'search-box',
  screen = 'results', // Screen name: 'results', 'empty', 'noResults'
  device = 'desktop', // Device type: 'desktop', 'mobile'
  mode = 'visual',
  compact = false,
  inspect = false,
  onNodeSelect,
  selectedNodeId,
  dragEnabled = false,
}) => {
  const t = useLocalizedMessage();
  const [surfaceWidth, setSurfaceWidth] = useState(null);
  const handleSurfaceResize = useCallback(({ width }) => setSurfaceWidth(width), []);
  // Construct the theme object for the widget
  // This theme is ISOLATED from the admin app theme
  // It mocks the customer's site theme for the widget elements
  const widgetTheme = useMemo(() => {
    const primaryColor = configTheme?.colors?.primary || configTheme?.brand || configTheme?.mainAccent || keaPalette.primary;
    const secondaryColor = configTheme?.colors?.secondary || '#f50057';

    return createTheme({
      palette: {
        primary: {
          main: primaryColor,
        },
        secondary: {
          main: secondaryColor,
        },
      },
      // We can add more theme mapping here from configTheme
    });
  }, [configTheme]);

  // Select Shell
  let ShellComponent = SearchBoxShell;
  if (viewType === 'resultsPage' || viewType === 'results-page') {
    ShellComponent = ResultsPageShell;
  } else if (viewType === 'dropdown' || viewType === 'searchDropdown' || viewType === 'search-dropdown' || viewType === 'classic') {
    ShellComponent = DropdownShell;
  }

  // Determine Frame to use
  const isMobile = device === 'mobile';
  const isDropdown = ShellComponent === DropdownShell;
  const presentation = isDropdown
    ? resolvePreviewFacetPresentation(viewConfig?.surface, surfaceWidth, isMobile)
    : undefined;
  useLayoutEffect(() => {
    if (onPresentationChange) onPresentationChange(presentation);
  }, [presentation, onPresentationChange]);
  const previewCanvas = useMemo(() => (viewConfig && isDropdown ? CanvasResolver.resolve(
    { search: { views: { dropdown: viewConfig } } },
    { view: 'dropdown', screen, device, visualSearch: false, presentation },
  ) : canvas), [viewConfig, isDropdown, screen, device, presentation, canvas]);

  if (compact) {
    return (
      <ThemeProvider theme={widgetTheme}>
        <div className={`${styles.compact} ${isMobile ? styles.mobile : ''}`}>
          <div className={styles.body} data-preview-canvas-area={dragEnabled ? canvas?.root : undefined} style={{ fontFamily: widgetTheme.typography.fontFamily, color: widgetTheme.palette.text.primary, '--primary-color': widgetTheme.palette.primary.main }}>
            <CanvasRenderer
              canvas={canvas}
              theme={widgetTheme}
              currentScreen={screen}
              currentDevice={device}
              mode={mode}
              inspect={inspect}
              onNodeSelect={onNodeSelect}
              selectedNodeId={selectedNodeId}
              dragEnabled={dragEnabled}
              illustrative
            />
          </div>
          <div className={styles.note}>{t('searchInterface.preview.sampleNote')}</div>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <Box
      data-testid="widget-preview-stage"
      data-device={device}
      width="100%"
      height="100%"
      display="flex"
      alignItems={isMobile ? 'flex-start' : 'center'}
      justifyContent="center"
      bgcolor="transparent"
      overflow="hidden"
      style={{
        padding: isMobile ? '18px' : '24px',
      }}
    >
      <ResponsiveFrame isMobile={isMobile} url="">
        <ShellComponent
          screen={device}
          {...(isDropdown ? {
            integration: viewConfig?.integration,
            onSurfaceResize: handleSurfaceResize,
          } : {})}
        >
          {/*
                        Wrap component in Box that resets some styles if needed,
                        and provides the specific theme.

                        The Shells render greyscale 'site' context.
                        The ThemeProvider here ensures 'Widget' components get the color.
                    */}
          <ThemeProvider theme={widgetTheme}>
            <Box
              id="widget-preview-root"
              height="100%"
              width="100%"
              style={{
                // Ensure the widget font doesn't inherit weird admin styles
                fontFamily: widgetTheme.typography.fontFamily,
                color: widgetTheme.palette.text.primary,
                // CSS Variables for potentially non-MUI components
                '--primary-color': widgetTheme.palette.primary.main,
                '--secondary-color': widgetTheme.palette.secondary.main,
              }}
            >
              <CanvasRenderer
                canvas={previewCanvas}
                theme={widgetTheme}
                currentScreen={screen}
                currentDevice={device}
                mode={mode}
                inspect={inspect}
                onNodeSelect={onNodeSelect}
                selectedNodeId={selectedNodeId}
                dragEnabled={dragEnabled}
                illustrative={inspect}
              />
            </Box>
          </ThemeProvider>
        </ShellComponent>
      </ResponsiveFrame>
    </Box>
  );
};

export default WidgetPreview;
